<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { ArrowLeft, ArrowRight, Ban, Pencil, Trash2, X } from 'lucide-vue-next'
import { formatApiError } from '@yuksalish/api-client'
import { formatDisplayDate, formatDisplayDateTime } from '@yuksalish/ui'
import { useAuthStore } from '@yuksalish/auth'
import { useProcurementApi } from '../composables/useProcurementApi'
import { isOpenSupplierInvoice, supplierInvoiceStatusClass } from '../types/procurement'
import type { SaveSupplierInvoicePayload, SupplierInvoice } from '../types/procurement'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const auth = useAuthStore()
const api = useProcurementApi()

const invoiceId = computed(() => Number(route.params.id ?? 0))
const permissions = computed(() => auth.user?.permissions ?? [])
const canUpdate = computed(() => permissions.value.includes('supplier_invoices.update'))
const canDelete = computed(() => permissions.value.includes('supplier_invoices.delete'))

const invoice = ref<SupplierInvoice | null>(null)
const loading = ref(true)
const error = ref('')

const actionError = ref('')
const actionMessage = ref('')

const showVoid = ref(false)
const voiding = ref(false)
const showDelete = ref(false)
const deleting = ref(false)

const canVoid = computed(() => canUpdate.value && invoice.value !== null && isOpenSupplierInvoice(invoice.value.status))

function money(value: number, currency: string | null | undefined): string {
  const formatted = new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0))

  return currency ? `${formatted} ${currency}` : formatted
}

function day(value: string | null): string {
  return value ? formatDisplayDate(value) : '—'
}

/** `PUT` replaces the record, so the whole invoice is resent with the new status. */
function voidPayload(current: SupplierInvoice): SaveSupplierInvoicePayload {
  return {
    supplier_agent_id: current.supplier_agent_id,
    purchase_order_id: current.purchase_order_id,
    invoice_number: current.invoice_number,
    invoice_date: (current.invoice_date ?? '').slice(0, 10),
    due_date: (current.due_date ?? '').slice(0, 10),
    currency_code: current.currency_code ?? '',
    exchange_rate: current.exchange_rate,
    invoice_amount: current.invoice_amount,
    department_agent_id: current.department_agent_id,
    warehouse_agent_id: current.warehouse_agent_id,
    notes: current.notes,
    status: 'void',
  }
}

async function load() {
  loading.value = true
  error.value = ''
  actionError.value = ''
  try {
    invoice.value = await api.getSupplierInvoice(invoiceId.value)
  } catch (cause) {
    error.value = formatApiError(cause)
  } finally {
    loading.value = false
  }
}

async function confirmVoid() {
  const current = invoice.value
  if (!current) return

  voiding.value = true
  actionError.value = ''
  try {
    invoice.value = await api.updateSupplierInvoice(current.id, voidPayload(current))
    actionMessage.value = t('procurement.invoice.voidSuccess')
    showVoid.value = false
  } catch (cause) {
    actionError.value = formatApiError(cause)
    showVoid.value = false
  } finally {
    voiding.value = false
  }
}

async function confirmDelete() {
  const current = invoice.value
  if (!current) return

  deleting.value = true
  actionError.value = ''
  try {
    await api.deleteSupplierInvoice(current.id)
    await router.push('/supplier-invoices')
  } catch (cause) {
    actionError.value = formatApiError(cause)
    deleting.value = false
    showDelete.value = false
  }
}

onMounted(load)
</script>

<template>
  <div v-if="loading" class="state-message"><p>{{ t('procurement.common.loading') }}</p></div>

  <div v-else-if="error" class="state-message error">
    <p>{{ error }}</p>
    <RouterLink class="btn-primary" to="/supplier-invoices">
      <ArrowLeft :size="15" aria-hidden="true" />
      {{ t('procurement.invoice.back') }}
    </RouterLink>
  </div>

  <template v-else-if="invoice">
    <div class="page-heading page-heading--actions">
      <div>
        <RouterLink class="back" to="/supplier-invoices">
          <ArrowLeft :size="14" aria-hidden="true" />
          {{ t('procurement.invoice.back') }}
        </RouterLink>
        <h2>{{ invoice.invoice_number }}</h2>
      </div>
      <div class="profile-actions">
        <RouterLink
          v-if="invoice.purchase_order_id"
          class="profile-action"
          :to="`/purchase-orders/${invoice.purchase_order_id}`"
        >
          {{ t('procurement.invoice.openOrder') }}
          <ArrowRight :size="15" aria-hidden="true" />
        </RouterLink>
        <RouterLink v-if="canUpdate" class="profile-action" :to="`/supplier-invoices/${invoice.id}/edit`">
          <Pencil :size="15" aria-hidden="true" />
          {{ t('procurement.invoice.edit') }}
        </RouterLink>
        <button v-if="canVoid" class="profile-action" type="button" @click="showVoid = true">
          <Ban :size="15" aria-hidden="true" />
          {{ t('procurement.invoice.void') }}
        </button>
        <button v-if="canDelete" class="profile-action danger" type="button" @click="showDelete = true">
          <Trash2 :size="15" aria-hidden="true" />
          {{ t('procurement.invoice.delete') }}
        </button>
      </div>
    </div>

    <p v-if="actionMessage" class="action-message">{{ actionMessage }}</p>
    <p v-if="actionError" class="error-inline">{{ actionError }}</p>

    <div class="detail-grid">
      <div class="panel">
        <h3>{{ t('procurement.invoice.summary') }}</h3>
        <dl class="detail-list">
          <div class="detail-row">
            <dt>{{ t('procurement.invoice.status') }}</dt>
            <dd>
              <span :class="['status-badge', supplierInvoiceStatusClass(invoice.status)]">
                {{ t(`procurement.invoiceStatus.${invoice.status}`) }}
              </span>
            </dd>
          </div>
          <div class="detail-row"><dt>{{ t('procurement.invoice.supplier') }}</dt><dd>{{ invoice.supplier_name || '—' }}</dd></div>
          <div class="detail-row">
            <dt>{{ t('procurement.invoice.order') }}</dt>
            <dd>
              <RouterLink v-if="invoice.purchase_order_id" class="link-action" :to="`/purchase-orders/${invoice.purchase_order_id}`">
                #{{ invoice.purchase_order_id }}
              </RouterLink>
              <template v-else>—</template>
            </dd>
          </div>
          <div class="detail-row">
            <dt>{{ t('procurement.invoice.amount') }}</dt>
            <dd>{{ money(invoice.invoice_amount, invoice.currency_code) }}</dd>
          </div>
          <div class="detail-row">
            <dt>{{ t('procurement.invoice.currency') }}</dt>
            <dd>{{ invoice.currency_code || '—' }} · {{ t('procurement.invoice.exchangeRate') }} {{ invoice.exchange_rate }}</dd>
          </div>
          <div class="detail-row"><dt>{{ t('procurement.invoice.notes') }}</dt><dd>{{ invoice.notes || '—' }}</dd></div>
        </dl>
      </div>

      <div class="panel">
        <h3>{{ t('procurement.invoice.invoiceDate') }}</h3>
        <dl class="detail-list">
          <div class="detail-row"><dt>{{ t('procurement.invoice.invoiceDate') }}</dt><dd>{{ day(invoice.invoice_date) }}</dd></div>
          <div class="detail-row"><dt>{{ t('procurement.invoice.dueDate') }}</dt><dd>{{ day(invoice.due_date) }}</dd></div>
          <div class="detail-row"><dt>{{ t('procurement.invoice.warehouse') }}</dt><dd>{{ invoice.warehouse_name || '—' }}</dd></div>
          <div class="detail-row"><dt>{{ t('procurement.invoice.department') }}</dt><dd>{{ invoice.department_name || '—' }}</dd></div>
          <div class="detail-row"><dt>{{ t('procurement.invoice.created') }}</dt><dd>{{ formatDisplayDateTime(invoice.created_at) }}</dd></div>
          <div class="detail-row"><dt>{{ t('procurement.invoice.updated') }}</dt><dd>{{ formatDisplayDateTime(invoice.updated_at) }}</dd></div>
        </dl>
      </div>
    </div>

    <!-- Void dialog -->
    <div v-if="showVoid" class="modal-overlay" @click.self="showVoid = false">
      <div class="modal">
        <h3>{{ t('procurement.invoice.voidTitle') }}</h3>
        <p class="form-note">{{ t('procurement.invoice.voidConfirm') }}</p>
        <div class="modal-actions">
          <button class="btn-secondary" type="button" @click="showVoid = false">
            <X :size="15" aria-hidden="true" />
            {{ t('procurement.common.cancel') }}
          </button>
          <button class="btn-primary" type="button" :disabled="voiding" @click="confirmVoid">
            <Ban :size="15" aria-hidden="true" />
            {{ voiding ? t('procurement.common.saving') : t('procurement.invoice.void') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Delete dialog -->
    <div v-if="showDelete" class="modal-overlay" @click.self="showDelete = false">
      <div class="modal">
        <h3>{{ t('procurement.invoice.deleteTitle') }}</h3>
        <p class="form-note">{{ t('procurement.invoice.deleteConfirm') }}</p>
        <div class="modal-actions">
          <button class="btn-secondary" type="button" @click="showDelete = false">
            <X :size="15" aria-hidden="true" />
            {{ t('procurement.common.cancel') }}
          </button>
          <button class="btn-primary" type="button" :disabled="deleting" @click="confirmDelete">
            <Trash2 :size="15" aria-hidden="true" />
            {{ deleting ? t('procurement.common.saving') : t('procurement.invoice.delete') }}
          </button>
        </div>
      </div>
    </div>
  </template>

  <div v-else class="panel">
    <span class="empty-inline">{{ t('procurement.invoice.notFound') }}</span>
  </div>
</template>
